import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";
import { tenantScopeSchema } from "../common/tenant";

export const ASSET_TYPES = [
  "REFRIGERATOR",
  "FREEZER",
  "COLD_ROOM",
  "DISPLAY_CASE",
  "TRANSPORT_UNIT",
  "STORAGE_ROOM",
  "SERVER_ROOM",
  "PRODUCTION_AREA",
  "TANK",
  "MEETING_ROOM",
  /** One conductor. The form asks for an ampere band and nothing else (ADR-102). */
  "CIRCUIT",
  "OTHER",
] as const;

export const assetTypeSchema = z.enum(ASSET_TYPES);
export type AssetType = z.infer<typeof assetTypeSchema>;

// =============================================================================
// Expected occupancy
// =============================================================================

/**
 * Whether a space has hours it is expected to be in use.
 *
 * Three states rather than a nullable schedule, because "nobody has
 * configured this" and "nobody should ever be in here" are different facts
 * and an absent schedule can only express the first. A locked stockroom
 * where any presence at all is an incident is a real configuration, and
 * without NEVER it is indistinguishable from an asset somebody forgot about
 * (ADR-069).
 */
export const OCCUPANCY_EXPECTATIONS = ["UNMONITORED", "NEVER", "SCHEDULED"] as const;
export const occupancyExpectationSchema = z.enum(OCCUPANCY_EXPECTATIONS);
export type OccupancyExpectation = z.infer<typeof occupancyExpectationSchema>;

/**
 * A local wall-clock time as `HH:MM`, in the organization's timezone.
 *
 * The same string `<input type="time">` produces, stored and compared
 * unchanged — no conversion anywhere on the path, for the reason distances
 * are millimetres end to end (ADR-060). Seconds are not accepted: an
 * occupancy boundary precise to the second is precision nobody has.
 */
export const timeOfDaySchema = z
  .string()
  .trim()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour time such as 08:00 or 19:30.");

/** ISO-8601 weekdays: 1 is Monday, 7 is Sunday. */
export const occupancyDaysSchema = z
  .array(z.number().int().min(1).max(7))
  .max(7)
  .refine((days) => new Set(days).size === days.length, { message: "List each day once." });

/**
 * The occupancy settings an asset would end up with, once a write is applied.
 *
 * Resolved values rather than a request body, for the reason the range
 * checks resolve theirs before comparing: "these agree with each other" is a
 * property of the result, and a PATCH that moves one field has to be judged
 * against the three it left alone.
 */
export interface OccupancySettings {
  occupancy: OccupancyExpectation;
  occupancyDays: number[];
  occupancyStart: string | null;
  occupancyEnd: string | null;
}

/**
 * Whether the occupancy settings agree with each other.
 *
 * SCHEDULED without days or hours would store a schedule the engine can
 * never evaluate, and hours alongside NEVER would show somebody a window
 * that is silently ignored. Both are worse than a rejected request, because
 * both look configured.
 *
 * Equal ends are refused rather than read as "all day": a zero-length window
 * means the space is never expected in use, which is what NEVER is for, and
 * a twenty-four-hour one means presence is never an incident, which is what
 * UNMONITORED is for. Letting one pair of times mean either would make every
 * schedule ambiguous.
 */
export function occupancyProblem(
  value: OccupancySettings,
): { message: string; path: string } | null {
  const { occupancy, occupancyDays, occupancyStart, occupancyEnd } = value;

  if (occupancy === "SCHEDULED") {
    if (occupancyDays.length === 0) {
      return { message: "Choose the days the space is in use.", path: "occupancyDays" };
    }

    if (!occupancyStart || !occupancyEnd) {
      return { message: "Give both the start and the end of the hours.", path: "occupancyEnd" };
    }

    if (occupancyStart === occupancyEnd) {
      return {
        message:
          "The start and the end cannot be the same time. For a space never expected to be in use, choose \u201cNever\u201d instead.",
        path: "occupancyEnd",
      };
    }

    return null;
  }

  // Not SCHEDULED, so any hours here would be stored and never read.
  if (occupancyDays.length > 0 || occupancyStart || occupancyEnd) {
    return {
      message: "Hours only apply when the space is in use on a schedule.",
      path: "occupancy",
    };
  }

  return null;
}

/**
 * Plausible bounds for the environments this platform monitors, wide enough
 * for a blast freezer at one end and a production area at the other. This
 * catches a misplaced decimal point or a Fahrenheit value, not much else.
 */
export const TEMPERATURE_MIN_C = -80;
export const TEMPERATURE_MAX_C = 40;

export const temperatureSchema = z.number().min(TEMPERATURE_MIN_C).max(TEMPERATURE_MAX_C);
export const humiditySchema = z.number().min(0).max(100);

/**
 * The EM410-RDL's own measuring window, which the Semakiot listing gives as
 * 0.3 m to 12 m. Bounds outside it cannot be reached by the only distance
 * sensor in the registry, so accepting them would mean accepting a threshold
 * that can never fire. Expressed in millimetres, like everything else on this
 * path (ADR-060).
 */
export const DISTANCE_MIN_MM = 300;
export const DISTANCE_MAX_MM = 12_000;

export const distanceSchema = z.number().min(DISTANCE_MIN_MM).max(DISTANCE_MAX_MM);

/**
 * Ampere window a CT103-class clamp can be asked to watch.
 *
 * The registered model is rated 250 A. A threshold outside that cannot be
 * crossed by a reading this hardware is specified to take, so the form
 * refuses it (ADR-102).
 */
export const CURRENT_MIN_A = 0;
export const CURRENT_MAX_A = 250;

export const currentSchema = z.number().min(CURRENT_MIN_A).max(CURRENT_MAX_A);

export const assetSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  /** Denormalized for display, for the same reason as on Site. */
  organizationName: z.string(),
  siteId: z.uuid(),
  siteName: z.string(),
  name: z.string(),
  type: assetTypeSchema,
  targetTempMinC: z.number().nullable(),
  targetTempMaxC: z.number().nullable(),
  targetHumidityMin: z.number().nullable(),
  targetHumidityMax: z.number().nullable(),
  /**
   * The minimum is the closest the liquid may come to the sensor, so crossing
   * it means too full; the maximum is how far it may get before too empty.
   */
  targetDistanceMinMm: z.number().nullable(),
  targetDistanceMaxMm: z.number().nullable(),
  /** RMS amperes. The minimum is too little current; the maximum is too much. */
  targetCurrentMinA: z.number().nullable(),
  targetCurrentMaxA: z.number().nullable(),
  /** When the space is expected to be in use, and therefore when presence is an incident. */
  occupancy: occupancyExpectationSchema,
  occupancyDays: z.array(z.number().int()),
  occupancyStart: z.string().nullable(),
  occupancyEnd: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deviceCount: z.number().int().nonnegative(),
});

export type Asset = z.infer<typeof assetSchema>;

export const createAssetRequestSchema = z
  .object({
    siteId: z.uuid(),
    name: z.string().trim().min(2).max(120),
    type: assetTypeSchema,
    // Optional, and either both or neither: an asset can be monitored for the
    // record with no range enforced against it, but half a range would silently
    // never trigger.
    targetTempMinC: temperatureSchema.optional(),
    targetTempMaxC: temperatureSchema.optional(),
    targetHumidityMin: humiditySchema.optional(),
    targetHumidityMax: humiditySchema.optional(),
    targetDistanceMinMm: distanceSchema.optional(),
    targetDistanceMaxMm: distanceSchema.optional(),
    targetCurrentMinA: currentSchema.optional(),
    targetCurrentMaxA: currentSchema.optional(),
    // Optional rather than defaulted, so the column default is the single
    // place UNMONITORED is written. An asset created before anybody thought
    // about presence is not being asserted to be empty; it is simply not
    // being judged on it (ADR-045).
    occupancy: occupancyExpectationSchema.optional(),
    occupancyDays: occupancyDaysSchema.optional(),
    occupancyStart: timeOfDaySchema.optional(),
    occupancyEnd: timeOfDaySchema.optional(),
    notes: z.string().trim().max(1_000).optional(),
  })
  .extend(tenantScopeSchema.shape)
  .refine(
    (value) => (value.targetTempMinC === undefined) === (value.targetTempMaxC === undefined),
    {
      message: "Give both ends of the temperature range, or neither.",
      path: ["targetTempMaxC"],
    },
  )
  // An inverted range would make every reading an excursion, or none at all.
  .refine(
    (value) =>
      value.targetTempMinC === undefined ||
      value.targetTempMaxC === undefined ||
      value.targetTempMinC < value.targetTempMaxC,
    {
      message: "The minimum temperature must be below the maximum.",
      path: ["targetTempMinC"],
    },
  )
  .refine(
    (value) => (value.targetHumidityMin === undefined) === (value.targetHumidityMax === undefined),
    {
      message: "Give both ends of the humidity range, or neither.",
      path: ["targetHumidityMax"],
    },
  )
  .refine(
    (value) =>
      value.targetHumidityMin === undefined ||
      value.targetHumidityMax === undefined ||
      value.targetHumidityMin < value.targetHumidityMax,
    {
      message: "The minimum humidity must be below the maximum.",
      path: ["targetHumidityMin"],
    },
  )
  .refine(
    (value) =>
      (value.targetDistanceMinMm === undefined) === (value.targetDistanceMaxMm === undefined),
    {
      message: "Give both ends of the distance range, or neither.",
      path: ["targetDistanceMaxMm"],
    },
  )
  .refine(
    (value) =>
      value.targetDistanceMinMm === undefined ||
      value.targetDistanceMaxMm === undefined ||
      value.targetDistanceMinMm < value.targetDistanceMaxMm,
    {
      message: "The minimum distance must be below the maximum.",
      path: ["targetDistanceMinMm"],
    },
  )
  .refine(
    (value) => (value.targetCurrentMinA === undefined) === (value.targetCurrentMaxA === undefined),
    {
      message: "Give both ends of the current range, or neither.",
      path: ["targetCurrentMaxA"],
    },
  )
  .refine(
    (value) =>
      value.targetCurrentMinA === undefined ||
      value.targetCurrentMaxA === undefined ||
      value.targetCurrentMinA < value.targetCurrentMaxA,
    {
      message: "The minimum current must be below the maximum.",
      path: ["targetCurrentMinA"],
    },
  )
  .superRefine((value, ctx) => {
    const problem = occupancyProblem({
      occupancy: value.occupancy ?? "UNMONITORED",
      occupancyDays: value.occupancyDays ?? [],
      occupancyStart: value.occupancyStart ?? null,
      occupancyEnd: value.occupancyEnd ?? null,
    });

    if (problem) {
      ctx.addIssue({ code: "custom", message: problem.message, path: [problem.path] });
    }
  });

export type CreateAssetRequest = z.infer<typeof createAssetRequestSchema>;

/**
 * Editing an asset is how an administrator tunes the thresholds the alert
 * engine works from: the target range on the asset is the operative bound,
 * and a rule only overrides it when it carries one of its own (ADR-048).
 *
 * `null` clears a bound, which is different from omitting the field. Clearing
 * means "monitor this without a verdict"; omitting means "leave it alone".
 * The site is not editable here — devices derive their site from the asset,
 * so moving one is a relocation rather than a field edit.
 */
export const updateAssetRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    type: assetTypeSchema,
    targetTempMinC: temperatureSchema.nullable(),
    targetTempMaxC: temperatureSchema.nullable(),
    targetHumidityMin: humiditySchema.nullable(),
    targetHumidityMax: humiditySchema.nullable(),
    targetDistanceMinMm: distanceSchema.nullable(),
    targetDistanceMaxMm: distanceSchema.nullable(),
    targetCurrentMinA: currentSchema.nullable(),
    targetCurrentMaxA: currentSchema.nullable(),
    // Not nullable: there is no "unset" for an expectation, because
    // UNMONITORED already is one. The hours are nullable, and clearing them
    // is only coherent alongside a move away from SCHEDULED, which the
    // service checks against the stored row.
    occupancy: occupancyExpectationSchema,
    occupancyDays: occupancyDaysSchema,
    occupancyStart: timeOfDaySchema.nullable(),
    occupancyEnd: timeOfDaySchema.nullable(),
    notes: z.string().trim().max(1_000).nullable(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one field to update.",
  })
  // Only checked when both ends travel together: a patch touching one end of
  // a range is validated against the stored other end, in the service.
  .refine(
    (value) =>
      value.targetTempMinC === undefined ||
      value.targetTempMaxC === undefined ||
      value.targetTempMinC === null ||
      value.targetTempMaxC === null ||
      value.targetTempMinC < value.targetTempMaxC,
    { message: "The minimum temperature must be below the maximum.", path: ["targetTempMinC"] },
  )
  .refine(
    (value) =>
      value.targetHumidityMin === undefined ||
      value.targetHumidityMax === undefined ||
      value.targetHumidityMin === null ||
      value.targetHumidityMax === null ||
      value.targetHumidityMin < value.targetHumidityMax,
    { message: "The minimum humidity must be below the maximum.", path: ["targetHumidityMin"] },
  )
  .refine(
    (value) =>
      value.targetDistanceMinMm === undefined ||
      value.targetDistanceMaxMm === undefined ||
      value.targetDistanceMinMm === null ||
      value.targetDistanceMaxMm === null ||
      value.targetDistanceMinMm < value.targetDistanceMaxMm,
    { message: "The minimum distance must be below the maximum.", path: ["targetDistanceMinMm"] },
  )
  .refine(
    (value) =>
      value.targetCurrentMinA === undefined ||
      value.targetCurrentMaxA === undefined ||
      value.targetCurrentMinA === null ||
      value.targetCurrentMaxA === null ||
      value.targetCurrentMinA < value.targetCurrentMaxA,
    { message: "The minimum current must be below the maximum.", path: ["targetCurrentMinA"] },
  );

export type UpdateAssetRequest = z.infer<typeof updateAssetRequestSchema>;

export const listAssetsQuerySchema = listQuerySchema.extend({
  siteId: z.uuid().optional(),
  type: assetTypeSchema.optional(),
});

export type ListAssetsQuery = z.infer<typeof listAssetsQuerySchema>;

export const assetListSchema = paginatedSchema(assetSchema);
export type AssetList = z.infer<typeof assetListSchema>;
